import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { products } from '../data/products';
import { Sparkles, ArrowRight, RotateCcw } from 'lucide-react';

const QUESTIONS = [
  {
    key: 'concern',
    q: 'What matters most to you right now?',
    options: [
      { label: 'Calming stress or anxiety', tags: ['Calm', 'Anxiety', 'CBD'] },
      { label: 'Better, deeper sleep', tags: ['Sleep', 'CBN'] },
      { label: 'Energy, focus & performance', tags: ['Energy', 'Focus', 'Brain'] },
      { label: 'Immune & respiratory support', tags: ['Immune', 'Respiratory'] },
      { label: 'Body care & pampering', tags: ['Body Scrub', 'Massage Oil', 'Lip Care'] },
    ]
  },
  {
    key: 'form',
    q: 'How would you like to take it?',
    options: [
      { label: 'Under-the-tongue drops (fast-acting)', categories: ['Elixirs'] },
      { label: 'Capsules I can swallow', categories: ['Capsules'] },
      { label: 'Something I apply to my skin', categories: ['Skincare', 'Body Care'] },
      { label: 'I\u2019m open to anything', categories: ['Elixirs', 'Capsules', 'Skincare', 'Body Care', 'Sleep'] },
    ]
  },
  {
    key: 'moment',
    q: 'When will you use it?',
    options: [
      { label: 'Morning \u2014 to start strong', tags: ['Energy', 'Focus', 'Immune'] },
      { label: 'Midday \u2014 for balance', tags: ['Calm', 'Focus', 'Anxiety'] },
      { label: 'Evening \u2014 to wind down', tags: ['Sleep', 'Calming', 'Relaxing'] },
      { label: 'Whenever \u2014 as needed', tags: ['CBD'] },
    ]
  },
  {
    key: 'who',
    q: 'Who is this for?',
    options: [
      { label: 'Just me', tags: [] },
      { label: 'Me & my partner', tags: ['Massage Oil'] },
      { label: 'My pet', tags: ['Pets'] },
      { label: 'A friend / loved one (gift)', tags: ['Gift', 'Bundle', 'Gift Card'] },
    ]
  },
  {
    key: 'budget',
    q: 'What\u2019s your budget?',
    options: [
      { label: 'Under $15', maxPrice: 15 },
      { label: '$15 \u2013 $30', maxPrice: 30 },
      { label: '$30 \u2013 $60', maxPrice: 60 },
      { label: 'No limit', maxPrice: 999 },
    ]
  }
];

const scoreProduct = (product, answers) => {
  let score = 0;

  const concern = answers.concern;
  if (concern) concern.tags.forEach(t => { if (product.tags?.includes(t)) score += 5; });

  const form = answers.form;
  if (form && form.categories.includes(product.category)) score += 4;

  const moment = answers.moment;
  if (moment) moment.tags.forEach(t => { if (product.tags?.includes(t)) score += 3; });

  const who = answers.who;
  if (who) who.tags.forEach(t => { if (product.tags?.includes(t) || product.category === t) score += 4; });
  if (who && who.label.startsWith('My pet') && product.category !== 'Pets') score -= 6;
  if (who && !who.label.startsWith('My pet') && product.category === 'Pets') score -= 6;

  const budget = answers.budget;
  if (budget && product.price > budget.maxPrice) score -= 5;

  return score;
};

const RitualQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, [step, done]);

  const total = QUESTIONS.length;
  const currentQ = QUESTIONS[step];

  const choose = (option) => {
    const newAnswers = { ...answers, [currentQ.key]: option };
    setAnswers(newAnswers);
    if (step + 1 < total) {
      setStep(step + 1);
    } else {
      setDone(true);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setDone(false);
  };

  const back = () => setStep(Math.max(0, step - 1));

  const recommendations = React.useMemo(() => {
    if (!done) return [];
    return [...products]
      .map(p => ({ p, score: scoreProduct(p, answers) }))
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(x => x.p);
  }, [done, answers]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#efe9db] to-[#faf7f1]">
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        {!done ? (
          <>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-3">
                <Sparkles className="w-4 h-4" /> Ritual Quiz
              </div>
              <h1 className="font-serif-elegant text-4xl md:text-6xl mb-3">Find Your Remedy</h1>
              <p className="text-[#4a4640] max-w-lg mx-auto">Answer a few gentle questions and we&rsquo;ll point you to the herbs your body is asking for.</p>
            </div>

            <div className="mb-8">
              <div className="flex justify-between text-xs uppercase tracking-widest text-[#6f6a5f] mb-2">
                <span>Question {step + 1} of {total}</span>
                <span>{Math.round(((step + 1) / total) * 100)}%</span>
              </div>
              <div className="h-1 bg-[#e5decf]">
                <div className="h-1 bg-[#3a4a3b] transition-all duration-500" style={{ width: `${((step + 1) / total) * 100}%` }} />
              </div>
            </div>

            <div className="bg-[#faf7f1] p-8 md:p-12 shadow-sm">
              <h2 className="font-serif-elegant text-2xl md:text-3xl mb-8 text-center">{currentQ.q}</h2>
              <div className="space-y-3">
                {currentQ.options.map((opt) => (
                  <button
                    key={opt.label}
                    onClick={() => choose(opt)}
                    className="w-full text-left px-6 py-4 border border-[#c9c1ad] hover:border-[#3a4a3b] hover:bg-[#efe9db] transition-colors flex items-center justify-between group"
                  >
                    <span>{opt.label}</span>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={back} className="mt-6 text-sm text-[#6f6a5f] hover:text-[#3a4a3b] link-underline">&larr; Back</button>
              )}
            </div>
          </>
        ) : (
          <div>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-3">
                <Sparkles className="w-4 h-4" /> Your ritual
              </div>
              <h1 className="font-serif-elegant text-4xl md:text-6xl mb-3">Herbs made for you</h1>
              <p className="text-[#4a4640] max-w-lg mx-auto">Based on your answers, here are the remedies we think you&rsquo;ll love.</p>
            </div>

            {recommendations.length === 0 ? (
              <div className="bg-[#faf7f1] p-10 text-center">
                <p className="text-[#4a4640] mb-6">Hmm, we couldn&rsquo;t find a perfect match. Browse our full shop and we&rsquo;re sure something will call to you.</p>
                <Link to="/collections/all" className="btn-primary">Shop All Products</Link>
              </div>
            ) : (
              <div className="space-y-6">
                {recommendations.map((p, i) => (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/products/${p.id}`)}
                    className="w-full text-left bg-[#faf7f1] hover:shadow-lg transition-shadow flex flex-col sm:flex-row gap-6 p-6 group"
                  >
                    <div className="relative w-full sm:w-40 h-40 bg-[#efe9db] shrink-0 overflow-hidden">
                      <div className="absolute top-2 left-2 z-10 badge-sale">Match #{i + 1}</div>
                      <img src={p.images[0]} alt={p.title} className="w-full h-full object-contain p-2" />
                    </div>
                    <div className="flex-1 flex flex-col justify-center">
                      <div className="uppercase tracking-widest text-xs text-[#7a8b6b] mb-1">{p.category}</div>
                      <div className="font-serif-elegant text-2xl mb-2">{p.title}</div>
                      <p className="text-sm text-[#4a4640] line-clamp-2 mb-3">{p.tagline || p.description}</p>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-medium">${p.price.toFixed(2)}</span>
                        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#3a4a3b] group-hover:gap-2 transition-all">
                          View <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <button onClick={reset} className="btn-outline inline-flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Retake quiz</button>
              <Link to="/collections/all" className="btn-primary">Browse all products</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RitualQuiz;
